import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

  constructor(
    private injector: Injector,
    private router: Router,
    private toastr: ToastrService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isOurApi = environment.apiUrl
      ? req.url.startsWith(environment.apiUrl)
      : (req.url.startsWith('/public/') || req.url.startsWith('/private/') || req.url.includes('conectaoficina.com.br'));

    if (!isOurApi) {
      return next.handle(req);
    }

    const isPublic = req.url.includes('/public/');
    const isPrivateCms = req.url.includes('/private/cms/');
    const isPrivateApp = req.url.includes('/private/app/');

    // Para requisições da nossa API, sempre enviamos cookies (withCredentials) e CSRF se for alteração
    const enhancedReq = this.addExtraRequestData(req);

    return next.handle(enhancedReq).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401 && (isPrivateCms || isPrivateApp)) {
          // Se for uma rota privada e falhou com 401, tenta refresh
          return this.handle401Error(req, next);
        }
        return throwError(() => err);
      })
    );
  }

  private addExtraRequestData(req: HttpRequest<any>): HttpRequest<any> {
    const authService = this.injector.get(AuthService);
    const token = authService.getToken();

    const cloneParams: any = {
      withCredentials: true
    };

    const headers: { [key: string]: string } = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const isWriteRequest = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method);
    if (isWriteRequest) {
      const csrfToken = authService.getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      } else {
        console.warn('[AuthInterceptor] CSRF token not found for write request:', req.method, req.url);
      }
    }

    if (Object.keys(headers).length > 0) {
      cloneParams.setHeaders = headers;
    }

    return req.clone(cloneParams);
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const authService = this.injector.get(AuthService);
      return authService.refreshToken().pipe(
        switchMap((response) => {
          this.isRefreshing = false;
          if (response && response.status === 'SUCCESS') {
            this.refreshTokenSubject.next(true);
            return next.handle(this.addExtraRequestData(request));
          }

          this.toastr.error('Sua sessão expirou. Faça login novamente.');
          authService.logout();
          return throwError(() => new Error('Session expired'));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.toastr.error('Sua sessão expirou. Faça login novamente.');
          authService.logout();
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(() => {
          return next.handle(this.addExtraRequestData(request));
        })
      );
    }
  }
}
